import React, { useEffect, useState } from 'react'
import { t } from 'i18next'
import { ShopCheckoutSideBarResponse, useShopCart } from 'src/Libs/shop'
import { useTypedSelector } from 'src/State/RootReducer'
import useModals from 'src/Hooks/useModals'
import { ProductGeneralType, ProductType, ProgressBarSteps } from 'src/Libs/shop/types'
import { useEffectShopAccess } from 'src/Libs/shop/hooks'
import { getBasketNormalConfigurationIds } from 'src/Libs/shop/utils'
import SystemConfiguration from '../SystemConfiguration'
import { useNormalSystemConfigurationContext } from 'src/Libs/shop/context/use-shop-normal-system-configuration-context'
import NormalBasicSystemWrapper from './BasicSystem/NormalBasicSystemWrapper'
import NormalMainComponent from './MainComponent/NormalMainComponent'
import NormalAdditionalComponent from './AdditionalComponent/NormalAdditionalComponent'
import NormalBottomProgressBar from './BottomProgressBar/NormalBottomProgressBar'
import NormalSystemConfigurationCard from './SystemConfigurationCard/NormalSystemConfigurationCard'

/*
 * Wrapper for normal system configurator handling logic and step rendering
 */
const NormalSystemConfigurationWrapper: React.FC = () => {
  const { openShopHintModal } = useModals()
  const { getCheckoutProducts } = useShopCart()
  const basketQuantity = useTypedSelector(state => state.shop.basketQuantity)
  const { activePage, handleProgress, allowContinueMain } = useNormalSystemConfigurationContext()

  const [cartData, setCartData] = useState<ShopCheckoutSideBarResponse | null>(null)

  const hasBasketSystemConfigurator = cartData?.lineItems.find(product => product.type === ProductGeneralType.SYSTEM_CONFIGURATOR)
  const hasBasketRetrofitBattery = cartData?.lineItems.some(product => product.payload?.e3dc?.productType === ProductType.RetrofitBattery)
  const existingQntmSystemConfiguration = cartData?.lineItems.find(product => product.type === ProductGeneralType.QNTM_SYSTEM_CONFIGURATOR)

  const cartDesiredShippingDate = cartData?.extensions?.e3dcDesiredShippingDate ?? null
  const existingSystemConfigurationDate = hasBasketSystemConfigurator ? cartDesiredShippingDate : null
  const existingSystemConfigurationIds = getBasketNormalConfigurationIds(cartData)
  const existingSystemConfigurationType = hasBasketSystemConfigurator?.payload.quantityType

  useEffect(() => {
    if (activePage === ProgressBarSteps.BasicSystem) {
      if (existingQntmSystemConfiguration) {
        return openShopHintModal({
          text: t('shop.note.basket-has-quantum-configuration'),
        })
      }
      if (hasBasketRetrofitBattery) {
        return openShopHintModal({
          text: t('shop.note.basket-has-retrofit-battery'),
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, hasBasketRetrofitBattery, existingQntmSystemConfiguration])

  useEffectShopAccess(() => {
    if (activePage === ProgressBarSteps.BasicSystem) {
      getCheckoutProducts()
        .then(result => {
          if (result) {
            setCartData(result)
          }
        })
        .catch(error => console.error(error))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basketQuantity, activePage])

  const steps = (): React.ReactNode => {
    switch (activePage) {
      case ProgressBarSteps.BasicSystem:
        return (
          <NormalBasicSystemWrapper
            existingSystemConfigurationType={existingSystemConfigurationType}
            desiredShippingDate={existingSystemConfigurationDate}
            cartSystemConfigurationIds={existingSystemConfigurationIds}
          />
        )

      case ProgressBarSteps.MainComponent:
        return <NormalMainComponent />

      case ProgressBarSteps.AdditionalComponent:
        return <NormalAdditionalComponent />

      default:
        return null
    }
  }

  const renderSidebarCard = (onClose: () => void): React.ReactNode => (
    <NormalSystemConfigurationCard showMobile={false} onClose={onClose} navigateTo={handleProgress} progress={activePage} />
  )

  const renderMobileSidebarCard = (onClose: () => void): React.ReactNode => (
    <NormalSystemConfigurationCard showMobile onClose={onClose} navigateTo={handleProgress} progress={activePage} />
  )

  return (
    <SystemConfiguration
      content={steps()}
      bottomBar={<NormalBottomProgressBar progress={activePage} handleProgress={handleProgress} allProductsSelected={allowContinueMain} handleBack />}
      sidebarCard={renderSidebarCard}
      mobileSidebarCard={renderMobileSidebarCard}
    />
  )
}

export default NormalSystemConfigurationWrapper
